/**
 * VTO Bridge — cableado del DOM.
 *
 * Traduce los mensajes del iframe del Probador Virtual a acciones del tema. La lógica
 * que decide algo vive en vto-bridge-core.js, que se prueba con `node --test`.
 *
 * Contrato: el iframe sólo EMITE; no escucha nada. Mensajes:
 *   VTO_ADD_TO_CART   {variantId, productId}
 *   VTO_DOWNLOAD_IMAGE {url}
 *   VTO_OPEN_PRIVACY  (sin payload)
 *
 * Spec: docs/superpowers/specs/2026-08-15-vto-shopify-port-design.md §6.3, §6.4, §6.5
 */
(function () {
  'use strict';

  var Core = window.VtoBridgeCore;

  if (!Core) {
    console.error('[VTO] vto-bridge-core.js no cargó; el probador virtual queda inactivo.');
    return;
  }

  var CLOSE_DELAY_MS = 500;

  /**
   * Los 15 parámetros que manda el tema, casi los mismos que la app (tapcardPDP.jsx:969-988)
   * salvo customerEmail (ver abajo).
   *
   * OJO con las convenciones: la página hospedada mezcla estilos a propósito
   * (vto/vto-view.html:41-61). product_id, product_image, variant_id y open_vto van en
   * snake_case; productTitle, variantTitle, color, productCode, barcode, collection,
   * customerId, customerName, os, appVersion y deviceId van en camelCase.
   * Escribir product_title en vez de productTitle NO falla: la prenda se muestra como
   * el literal "Tapcart Product".
   *
   * NO se manda customerEmail: en Liquid `customer` da id y correo juntos o ninguno, así que
   * en web el correo es redundante con customerId para la atribución del servidor (server.js
   * usa customerId o customerEmail indistintamente). Quitarlo evita dejarlo en el DOM de una
   * tienda que carga FigPii/Heap/GTM/Rebuy/Nosto/Klaviyo -- herramientas que serializan el DOM.
   * La app de Tapcart sí lo manda (construye su propia URL desde código independiente), así
   * que la atribución ahí no se ve afectada.
   */
  function readParams(frame) {
    var data = frame.dataset;
    return {
      product_id: data.vtoProductId,
      product_image: Core.absoluteImageUrl(data.vtoProductImage, window.location.origin),
      variant_id: data.vtoVariantId,
      productTitle: data.vtoProductTitle,
      variantTitle: data.vtoVariantTitle,
      color: data.vtoColor,
      productCode: data.vtoProductCode,
      barcode: data.vtoBarcode,
      collection: data.vtoCollection,
      customerId: data.vtoCustomerId,
      customerName: data.vtoCustomerName,
      os: 'web',
      appVersion: data.vtoAppVersion,
      deviceId: Core.getOrCreateDeviceId(Core.safeLocalStorage(), Core.randomId),
      open_vto: 'true'
    };
  }

  function setup(modal) {
    var frame = modal.querySelector('[data-vto-frame]');
    var errorBox = modal.querySelector('[data-vto-error]');
    var form = document.getElementById(modal.getAttribute('data-vto-form-id'));
    var variantInput = form ? form.querySelector('[data-vto-variant-input]') : null;
    var privacyUrl = modal.getAttribute('data-vto-privacy-url');
    var closeTimer = null;

    if (!frame || !form || !variantInput || !errorBox) {
      console.error('[VTO] faltan nodos del modal; se aborta el cableado.', modal.id);
      return;
    }

    function showError(message) {
      errorBox.textContent = message || 'No se pudo agregar a la bolsa. Intenta de nuevo.';
      errorBox.hidden = false;
    }

    function clearError() {
      errorBox.textContent = '';
      errorBox.hidden = true;
    }

    // modal.hide() (theme.js:1867-1872) es el método público equivalente a
    // removeAttribute('open'): internamente comprueba `if (!this.open) return` antes
    // de quitar el atributo, así que llamarlo dos veces (p. ej. un closeTimer
    // rezagado sobre un modal que alguien ya cerró) es seguro por sí solo.
    // El guard explícito de abajo cubre el caso contrario -- un closeTimer
    // rezagado de una sesión vieja que dispara justo cuando el modal YA está
    // cerrado -- para no depender únicamente del guard interno del tema. El botón
    // de cerrar no pasa por aquí: usa el is="dialog-close-button" del tema.
    function closeModal() {
      if (!modal.open) return;
      modal.hide();
    }

    // El page token vive 2 h y se acuña por carga de página: se monta el iframe al
    // abrir para que siempre esté fresco.
    //
    // El src se fija en dialog:after-show (NO before-show) y se limpia en
    // dialog:before-hide (NO after-hide) por el re-parenteo de Modal.shouldAppendToBody
    // (theme.js:2133): dialog:before-show se despacha ANTES de document.body.append(this)
    // (theme.js:1978-1981), y re-parentar un <iframe> destruye y recrea su browsing
    // context -- si el src ya estuviera fijado ahí, el navegador re-navegaría a él tras
    // el append, cargando la página del VTO dos veces. dialog:after-show se despacha
    // después de terminada la animación de entrada (theme.js:1987-1989), ya con el
    // modal en su ubicación final, así que fijar el src ahí sólo navega una vez.
    // Simétricamente, dialog:before-hide se despacha ANTES de devolver el modal a su
    // padre original (theme.js:2001-2007): si limpiamos ahí, el re-parenteo posterior
    // re-navega a 'about:blank' (inofensivo) en vez de arrancar una tercera carga real.
    modal.addEventListener('dialog:before-show', function () {
      clearError();
    });

    modal.addEventListener('dialog:after-show', function () {
      frame.src = Core.buildVtoUrl(frame.dataset.vtoBase, readParams(frame));
    });

    // Guard: dialog:before-hide llega también cuando la promesa de una animación de
    // salida VIEJA resuelve sobre un modal que mientras tanto se volvió a abrir
    // (abrir -> Escape -> tocar la píldora de nuevo a los ~100ms). Si para cuando este
    // handler corre el modal ya está abierto otra vez, no lo vaciamos.
    modal.addEventListener('dialog:before-hide', function () {
      if (modal.open) return;
      if (closeTimer) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
      }
      frame.src = 'about:blank';
      clearError();
    });

    function handleAddToCart(payload) {
      var variantId = Core.normalizeVariantId(payload && payload.variantId);
      if (!variantId) {
        showError('No se pudo identificar la talla seleccionada.');
        return;
      }
      clearError();
      variantInput.value = variantId;
      // form.requestSubmit() no existe en Safari/iOS < 16. El tema NUNCA lo usa: para
      // disparar un submit programático despacha `new Event('submit', {bubbles:true})`
      // (p. ej. theme.js:2262, facets-drawer). ProductForm (theme.js:2775-2779) escucha
      // "submit" con addEventListener, así que un Event sintético lo dispara igual.
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }

    function handleDownload(url) {
      // Defensa en profundidad: el origen del VTO es de confianza (isTrustedVtoMessage ya lo
      // validó), pero esa página hospedada carga Tailwind desde un CDN de terceros -- si algo
      // ahí se comprometiera, podría emitir una URL arbitraria (javascript:, data:, etc.) que
      // este código pasaría sin más a fetch()/window.open(). Exigir https:// no depende de que
      // el propio VTO se comporte.
      if (!url || url.indexOf('https://') !== 0) return;
      shareImage(url).catch(function (error) {
        // navigator.share() rechaza con AbortError cuando el usuario DESCARTA el
        // sheet de compartir; eso no es un fallo real y no debe abrir una pestaña.
        if (error && error.name === 'AbortError') return;
        window.open(url, '_blank', 'noopener');
      });
    }

    async function shareImage(url) {
      if (!navigator.share || !navigator.canShare) throw new Error('sin Web Share');
      var response = await fetch(url, { mode: 'cors' });
      if (!response.ok) throw new Error('descarga fallida');
      var blob = await response.blob();
      var file = new File([blob], 'ivonne-probador-virtual.jpg', {
        type: blob.type || 'image/jpeg'
      });
      if (!navigator.canShare({ files: [file] })) throw new Error('no comparte archivos');
      await navigator.share({ files: [file] });
    }

    window.addEventListener('message', function (event) {
      if (!Core.isTrustedVtoMessage(event, frame.contentWindow)) return;

      switch (event.data.type) {
        case 'VTO_ADD_TO_CART':
          handleAddToCart(event.data.payload);
          break;
        case 'VTO_DOWNLOAD_IMAGE':
          handleDownload(event.data.payload && event.data.payload.url);
          break;
        case 'VTO_OPEN_PRIVACY':
          window.location.assign(privacyUrl);
          break;
        default:
          break;
      }
    });

    // El tema despacha variant:add al agregar bien, y cart:error al fallar.
    form.addEventListener('variant:add', function () {
      // Dos variant:add seguidos (dos agregados rápidos) no deben dejar un timer
      // huérfano capaz de cerrar un modal ya reabierto para otro producto/talla.
      if (closeTimer) {
        window.clearTimeout(closeTimer);
      }
      closeTimer = window.setTimeout(closeModal, CLOSE_DELAY_MS);
    });

    // El iframe ya dijo "Agregado a la bolsa" y no se puede desdecir; lo único que se
    // puede hacer es NO cerrar el modal y desmentirlo aquí. Spec §6.4.
    form.addEventListener('cart:error', function (event) {
      showError(event.detail && event.detail.error);
    });
  }

  // Marca cada modal ya cableado con data-vto-wired para que una re-ejecución de
  // init() (shopify:section:load) no vuelva a añadir los mismos listeners.
  function init() {
    var modals = document.querySelectorAll('[data-vto-modal]:not([data-vto-wired])');
    Array.prototype.forEach.call(modals, function (modal) {
      modal.setAttribute('data-vto-wired', '');
      setup(modal);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // En el editor de temas, cada edición de la sección "producto principal" vuelve a
  // renderizar el snippet y produce un <x-modal> nuevo (y sin cablear) en el DOM.
  // Sin este listener, ese modal nuevo se queda mudo y el merchant ve una tarjeta
  // en blanco al abrirlo.
  document.addEventListener('shopify:section:load', init);
})();
